// Запросы к api. Ошибки приходят человеческим текстом из api — показываем как есть.
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T = unknown>(path: string, body?: unknown, method?: string): Promise<T> {
  // Файл или запись (Blob) уходит как есть, со своим типом; остальное — JSON
  const raw = body instanceof Blob;
  const res = await fetch(path, {
    method: method ?? (body === undefined ? 'GET' : 'POST'),
    headers:
      body === undefined
        ? {}
        : { 'content-type': raw ? (body as Blob).type || 'application/octet-stream' : 'application/json' },
    body: body === undefined ? undefined : raw ? (body as Blob) : JSON.stringify(body),
    credentials: 'same-origin',
  });
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    window.dispatchEvent(new CustomEvent('aaff:unauthorized'));
  }
  if (!res.ok) {
    const msg = (data as { error?: string } | null)?.error ?? 'Не получилось. Попробуйте ещё раз.';
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export interface ThreadSummary {
  id: string;
  title: string | null;
  context: { kind: string; label: string } | null;
  model: string;
  lastMessageAt: string | null;
  createdAt: string;
  /** Когда убран в архив (список архива). */
  archivedAt?: string | null;
  working: boolean;
  /** Итог разговора: агент подводит его после паузы и записывает договорённости в память. */
  summary?: { headline: string | null; text: string; at: string | null } | null;
}

export interface SubscriptionWindow {
  kind: string;
  utilization: number;
  resetsAt: string | null;
  status: string | null;
}

export interface AlertView {
  id: string;
  key: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  body: string | null;
  action: { label: string; to: string } | null;
  createdAt: string;
}

export interface ClaudeAccess {
  state: 'unknown' | 'ok' | 'lost';
  since: string | null;
  checkedAt: string | null;
  message: string | null;
}

export interface ClaudeToken {
  hint: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  daysLeft: number | null;
}

export interface AgentStatus {
  runner: boolean;
  runnerId: string | null;
  activeRuns: number;
  working: number;
  workingThreads: string[];
  pendingPermissions: number;
  /** Отдаёт ли Claude Code лимиты без ответа модели; false — только после ответов агента. */
  usageAvailable: boolean | null;
  subscription: SubscriptionWindow[];
  /** Доступ к подписке Claude: проверяется каждые 12 минут. */
  access?: ClaudeAccess;
  claudeToken?: ClaudeToken | null;
  alerts?: AlertView[];
  /** Очередь фоновой работы агента: сколько ждёт, сколько работает, до какого времени лимит. */
  queue?: { waiting: number; background: number; limitUntil: string | null; reasons: Record<string, number> };
  /** Предложения агента, ждущие владельца (заявки и советы без отметки). */
  openProposals?: number;
}

export interface PendingPermission {
  id: string;
  threadId: string | null;
  threadTitle: string | null;
  toolName: string;
  input: Record<string, unknown>;
  status: string;
  createdAt: string;
  expiresAt: string;
  decidedAt: string | null;
}
